import { useCallback, useMemo, useRef, useState } from 'react';
import { getLaunchPlan } from '@repo/front-end-services';
import type { FloorPlannerLayout } from '@repo/frontend-types';

/** One launch's plan, as the page has read it. */
export type LaunchPlanRead =
  | { state: 'loading' }
  | { state: 'ready'; plan: FloorPlannerLayout }
  | { state: 'failed' };

export interface LaunchPlans {
  /** What is known of a launch's plan; null for one never asked for. */
  read: (uuid: string) => LaunchPlanRead | null;
  /**
   * Read a launch's plan unless it is read or being read. A failed one is read again: a dialog
   * opening asks afresh, as `useDraftChanges` did before the cache was shared.
   */
  request: (uuid: string) => void;
  /** *Try again*: read a failed launch's plan again; anything else is left as it is. */
  retry: (uuid: string) => void;
}

export interface UseLaunchPlansParams {
  middlewareApi: string;
  accessToken: string | undefined;
  twinId: number | undefined;
}

/**
 * The page's one cache of launch plans, keyed by launch uuid (`twin-details-page-load-launch`, R5.1)
 * — Versions, Discard and Load read production's plan once between them. A completed launch's plan
 * never changes, so an entry never goes stale; the cache is emptied when the twin changes, and a
 * read still in flight for the last twin is dropped when it lands.
 *
 * Every read is the existing bounded `getLaunchPlan` (30 s, no retry, `MIDDLEWARE_REQUEST_TIMEOUT_MS`).
 * A failure is said as one — never an empty plan — until *Try again*, or the next dialog to ask,
 * reads that one launch again.
 */
export const useLaunchPlans = ({
  middlewareApi,
  accessToken,
  twinId,
}: UseLaunchPlansParams): LaunchPlans => {
  const entries = useRef(new Map<string, LaunchPlanRead>());
  /** Bumped by each change of twin, so a read started for the last twin says nothing. */
  const generation = useRef(0);
  const twinRef = useRef(twinId);
  if (twinRef.current !== twinId) {
    twinRef.current = twinId;
    generation.current += 1;
    entries.current = new Map();
  }
  const [version, setVersion] = useState(0);

  const request = useCallback(
    (uuid: string) => {
      const known = entries.current.get(uuid)?.state;
      if (!accessToken || twinId === undefined || known === 'loading' || known === 'ready') return;
      const gen = generation.current;
      const map = entries.current;
      map.set(uuid, { state: 'loading' });
      setVersion(v => v + 1);
      const fail = (detail: { status: number | null; error?: string } | { error: string }) => {
        console.warn('[versions] launch plan read failed', { twinId, launchUuid: uuid, ...detail });
        if (gen !== generation.current) return;
        map.set(uuid, { state: 'failed' });
        setVersion(v => v + 1);
      };
      // `getLaunchPlan` resolves its failures rather than rejecting; the catch is for what it cannot.
      Promise.resolve(getLaunchPlan(middlewareApi, accessToken, twinId, uuid))
        .then(outcome => {
          if (outcome?.status !== 'found') {
            // The service's own reason, so a malformed answer (no status) is told from a timeout.
            fail({ status: outcome?.httpStatus ?? null, error: outcome?.error });
            return;
          }
          if (gen !== generation.current) return;
          map.set(uuid, { state: 'ready', plan: outcome.launch.plan });
          setVersion(v => v + 1);
        })
        .catch(error => fail({ error: error instanceof Error ? error.message : String(error) }));
    },
    [middlewareApi, accessToken, twinId],
  );

  const retry = useCallback(
    (uuid: string) => {
      if (entries.current.get(uuid)?.state === 'failed') request(uuid);
    },
    [request],
  );

  // A new object on every change, so a consumer's memo or effect reading through it re-runs.
  return useMemo(
    () => ({ read: (uuid: string) => entries.current.get(uuid) ?? null, request, retry }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the change signal
    [request, retry, version, twinId],
  );
};
